import React, { useState, useEffect, useMemo, Fragment } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import WarningIcon from '@mui/icons-material/Warning';
import ImageIcon from '@mui/icons-material/Image';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Typography, Divider, CardContent, CardMedia, CircularProgress, Avatar } from '@mui/material'; // Added CircularProgress, Avatar, Box
import Swal from "sweetalert2";
import { ServerBaseUrlForStaticImages } from "constants";
import CancelIcon from "@mui/icons-material/Cancel";
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EditServiceModal from './edit_services';
import AddServiceProviderForm from "./add_services";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { Tabs, Tab } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import BrokenImage from '@mui/icons-material/BrokenImage';


// Re-using AvatarWithLoader from the previous context to handle image loading states
const BASE_IMAGE_URL = ServerBaseUrlForStaticImages;

const AvatarWithLoader = ({ src, alt }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);

    useEffect(() => {
        if (src) {
            setLoading(true);
            setError(false);
            setHasLoadedSuccessfully(false);
        } else {
            setLoading(false);
            setError(true);
            setHasLoadedSuccessfully(false);
        }
    }, [src]);

    const handleImageLoad = () => {
        setLoading(false);
        setError(false);
        setHasLoadedSuccessfully(true);
    };

    const handleImageError = () => {
        setLoading(false);
        setError(true);
        setHasLoadedSuccessfully(false);
    };

    const shouldShowLoader = loading && !error && !hasLoadedSuccessfully;
    const hideActualImage = (loading && !hasLoadedSuccessfully) || error;

    if (!src) {
        return (
            <Avatar
                alt={alt}
                sx={{ width: 40, height: 40, margin: "auto", bgcolor: 'grey.300' }}
                variant="rounded"
            >
                <BrokenImage color="action" sx={{ fontSize: 24 }} />
            </Avatar>
        );
    }

    return (
        <Box sx={{
            position: 'relative',
            width: 40,
            height: 40,
            margin: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
        }}>
            {shouldShowLoader && (
                <CircularProgress size={20} sx={{ position: 'absolute', color: 'info.main' }} />
            )}
            {error && (
                <Avatar
                    alt={alt}
                    sx={{ width: 40, height: 40, margin: "auto", bgcolor: 'error.light' }}
                    variant="rounded"
                >
                    <BrokenImage sx={{ fontSize: 24, color: 'error.dark' }} />
                </Avatar>
            )}
            {!error && (
                <Avatar
                    src={src}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    sx={{
                        width: 40,
                        height: 40,
                        margin: "auto",
                        variant: "rounded",
                        visibility: hideActualImage ? 'hidden' : 'visible',
                        position: 'absolute',
                    }}
                    variant="rounded"
                />
            )}
        </Box>
    );
};


function ServiceRegistration() {
    const [page, setPage] = useState(0); // Add page state
    const [rowsPerPage, setRowsPerPage] = useState(10); // Add rowsPerPage state
    const [searchQuery, setSearchQuery] = useState(""); // Add searchQuery state

    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [Input, setInput] = useState({});
    const [editInput, setEditInput] = useState({});
    const [deleteProductId, setDeleteProductId] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [selectedImages2, setSelectedImages2] = useState([]);
    const [selectedImages3, setSelectedImages3] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [loading, setLoading] = useState(true);
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]); // Added for search filtering
    const email = localStorage.getItem("email");
    const [feedback, setFeedback] = useState("");
    const [suspendUntil, setSuspendUntil] = useState(null);
    const [suspendReason, setSuspendReason] = useState("");
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalImageOpen, setIsImageModalOpen] = useState(false);
    const [fullImage, setFullImage] = useState(null);
    const [viewImages, setViewImages] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const navigate = useNavigate();

    const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

    const handleViewDetails = (vendor) => {
        setSelectedVendor(vendor);
        setIsModalOpen(true);
    };

    const handleViewDetailsImages = (vendor) => {
        setViewImages(vendor);
        setIsImageModalOpen(true);
    };

    const handleImageCloseModal = () => {
        setIsImageModalOpen(false);
        setSelectedVendor(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedVendor(null);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        setSelectedVendor(null);
        fetchServices();
    };

    const columns = useMemo(() => [ // Wrap columns in useMemo
        {
            Header: "S.No",
            accessor: "s_no",
            align: "center",
            Cell: ({ row }) => row.index + 1, // Correct way to get serial number
        },
        {
            Header: "Name",
            accessor: "owner_name",
            sortable: true,
            align: "center",
        },
        {
            Header: "Service Type",
            accessor: "services_type",
            sortable: true,
            align: "center",
        },
        {
            Header: "Email",
            accessor: "email",
            sortable: false,
            align: "center",
        },
        {
            Header: "Profile Photo",
            accessor: "profile_photo",
            align: "center",
            Cell: ({ row }) => (
                <AvatarWithLoader
                    src={row.original.profile_photo || null}
                    alt={row.original.owner_name}
                />
            ),
        },
        {
            Header: "Options",
            accessor: "options",
            align: "center",
            Cell: ({ row }) => (
                <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                    <IconButton
                        color="success"
                        onClick={() => {
                            setOpenEditModal(true);
                            setEditInput(row.original);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => {
                            setDeleteProductId(row.original.email);
                            setOpenDeleteModal(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                    <IconButton
                        color="info"
                        onClick={() => handleViewDetails(row.original)}
                    >
                        <VisibilityIcon />
                    </IconButton>
                    <IconButton
                        color="success"
                        onClick={() => handleViewDetailsImages(row.original)}
                    >
                        <ImageIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => {
                            setDeleteProductId(row.original.email);
                            setOpenDeactivateModal(true);
                        }}
                    >
                        <CancelIcon />
                    </IconButton>
                </div>
            )
        }
    ], []);


    useEffect(() => {
        disableBackNavigation();
        fetchServices();
    }, []);

    useEffect(() => {
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            const newFilteredServices = services.filter(service => {
                return (
                    service.owner_name.toLowerCase().includes(lowerCaseQuery) ||
                    service.services_type.toLowerCase().includes(lowerCaseQuery) ||
                    service.email.toLowerCase().includes(lowerCaseQuery)
                );
            });
            setFilteredServices(newFilteredServices);
        } else {
            setFilteredServices(services);
        }
        setPage(0); // Reset page to 0 when search query changes
    }, [searchQuery, services]);


    const fetchServices = async () => {
        setLoading(true);
        try {
            const response = await api.get("/services_register/get_all_approved_services");
            console.log("Fetched services:", response);
            const fetchedServices = Array.isArray(response.data.services)
                ? response.data.services.map(service => ({
                    ...service,
                    experience_certificates: service.experience_certificates
                        ? service.experience_certificates
                            .split(",")
                            .map(img => img.trim().includes("http") ? img.trim() : `${ServerBaseUrlForStaticImages}/services_register/media/${img.trim()}`)
                            .join(",")
                        : "",
                    idproof_photo: service.idproof_photo
                        ? service.idproof_photo
                            .split(",")
                            .map(img => img.trim().includes("http") ? img.trim() : `${ServerBaseUrlForStaticImages}/services_register/media/${img.trim()}`)
                            .join(",")
                        : "",
                    profile_photo: service.profile_photo
                        ? `${ServerBaseUrlForStaticImages}/services_register/media/${service.profile_photo}`
                        : "",
                    work_photo: service.work_photo
                        ? `${ServerBaseUrlForStaticImages}/services_register/media/${service.work_photo}`
                        : "",
                    work_place_gps: service.work_place_gps
                        ? `${ServerBaseUrlForStaticImages}/services_register/media/${service.work_place_gps}`
                        : "",
                    work_video: service.work_video
                        ? `${ServerBaseUrlForStaticImages}/services_register/media/${service.work_video}`
                        : ""
                }))
                : [];

            setServices(fetchedServices);
            setFilteredServices(fetchedServices); // Initialize filtered services
            if (fetchedServices.length > 0) {
                setViewImages(fetchedServices[0]); // Set initial viewImages to the first service if available
            }

        } catch (error) {
            console.error("Error fetching services:", error);
            Swal.fire("Error", "Failed to load services. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditInput(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const deleteService = async (event) => {
        event.preventDefault();
        try {
            handleClose(); // Close the modal first
            const response = await api.put(`/services_register/delete_service_provider/${deleteProductId}`);
            if (response.data.status === "success") {
                Swal.fire({
                    title: 'Successfully Deleted Service Provider!',
                    icon: 'success',
                    timer: 1500
                });
                fetchServices();
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response.data.message,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error("Error deleting product:", error);
            Swal.fire({
                title: 'Error',
                text: 'An unexpected error occurred',
                icon: 'error',
            });
        }
    };

    const deactivateVendor = async (event) => {
        event.preventDefault();

        if (!suspendReason.trim()) {
            Swal.fire({
                title: 'Feedback Required',
                text: 'Please provide a reason for deactivation.',
                icon: 'warning',
            });
            return;
        }

        try {
            const response = await api.put(`/services_register/suspend_service/${deleteProductId}`, {
                suspend_until: suspendUntil ? suspendUntil.toISOString().split("T")[0] : null, // Format date as YYYY-MM-DD
                suspend_reason: suspendReason,
            });

            if (response.data.success) {
                Swal.fire({
                    title: 'Vendor Deactivated Successfully!',
                    icon: 'success',
                    timer: 1500
                });
                fetchServices();
                handleClose();
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response.data.message,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error("Error deactivating vendor:", error);
            Swal.fire({
                title: 'Error',
                text: 'An unexpected error occurred',
                icon: 'error',
            });
        }
    };

    const handleImageAdd = async (e, fieldName) => { // Removed email as it's in viewImages
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("email", viewImages.email);
        formData.append("field_name", fieldName);

        try {
            const res = await api.post("/services_register/add_vendor_image", formData);
            const newPath = res.data.file_path;

            const existing = viewImages[fieldName] ? viewImages[fieldName].replace(/\\/g, "/").split(",") : [];
            const updated = [...existing, newPath].join(",");
            setViewImages((prev) => ({ ...prev, [fieldName]: updated }));

            setSnackbar({ open: true, message: "Image uploaded successfully", severity: "success" });
            // setIsImageModalOpen(false); // No need to close modal after adding one image
            fetchServices(); // Refresh the list to reflect changes
        } catch (err) {
            console.error("Upload failed", err);
            setSnackbar({ open: true, message: "Upload failed", severity: "error" });
        }
    };

    const handleImageRemove = async (fieldName, index) => {
        const imageArray = viewImages[fieldName].replace(/\\/g, "/").split(",");
        const filePath = imageArray[index];

        const formData = new FormData();
        formData.append("email", viewImages.email);
        formData.append("field_name", fieldName);
        formData.append("file_path", filePath);

        try {
            await api.post("/services_register/remove_vendor_images", formData);
            const updated = imageArray.filter((_, i) => i !== index).join(",");
            setViewImages((prev) => ({ ...prev, [fieldName]: updated }));
            setSnackbar({ open: true, message: "Image removed successfully", severity: "success" });
            fetchServices(); // Refresh the list to reflect changes
        } catch (err) {
            console.error("Remove failed", err);
            setSnackbar({ open: true, message: "Remove failed", severity: "error" });
        }
    };

    const handleImageChange = async (event, imageType, mobileNumber) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("user_id", mobileNumber);
        formData.append("type", imageType);
        formData.append("file", file);

        try {
            const response = await api.put('services_register/upload_image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 1) {
                fetchServices();
                setIsImageModalOpen(false);
                Swal.fire({
                    title: "Image Updated!",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false,
                });
                const updatedFilePath = `${ServerBaseUrlForStaticImages}/services_register/media/${response.data.file_path}?t=${new Date().getTime()}`; // Use response.data.file_path

                setViewImages((prevState) => {
                    const updatedImages = { ...prevState, [imageType]: updatedFilePath };
                    return updatedImages;
                });
            } else {
                console.error("Upload error:", response.data.error);
                Swal.fire("Error", response.data.error || "Failed to upload image.", "error");
            }
        } catch (error) {
            console.error("Network error:", error);
            Swal.fire("Error", "An unexpected error occurred during image upload.", "error");
        }
    };

    const handleClose = () => {
        setInput({});
        setEditInput({});
        setSelectedImages([]);
        setSelectedImages2([]);
        setSelectedImages3([]);
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        setOpenDeactivateModal(false);
        setOpenAddModal(false);
        setFeedback("");
        setSuspendUntil(new Date());
        setSuspendReason("");
        fetchServices();
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const paginatedTableData = filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                                <MDTypography variant="h6" color="white">Service Provider Details</MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pl={3} pr={3}>
                                <TextField
                                    label="Search services..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ width: '300px' }}
                                />
                                <MDButton color="success" onClick={() => setOpenAddModal(true)}>Add Services</MDButton>
                            </MDBox>
                            <MDBox p={3} display="flex" justifyContent="center" alignItems="center" >
                                {loading ? (
                                    <CircularProgress color="primary" />
                                ) : (
                                    <DataTable
                                        table={{ columns: columns, rows: paginatedTableData }}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries={true}
                                        noEndBorder
                                        pagination={{
                                            variant: "contained",
                                            color: "dark",
                                            page: page,
                                            onPageChange: handlePageChange,
                                            rowsPerPage: rowsPerPage,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredServices.length,
                                        }}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <AddServiceProviderForm
                open={openAddModal} handleClose={handleCloseAddModal}
            />

            <EditServiceModal
                open={openEditModal}
                handleClose={handleClose}
                editInput={editInput}
                handleEditChange={handleEditChange}
            />

            <Dialog open={openDeleteModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deleteService}>
                    <DialogTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
                            </Grid>
                            <Grid item>
                                <MDTypography variant="h6" component="span">
                                    Confirm Delete
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent>
                        <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
                            <MDTypography variant="body1">
                                Are you sure you want to delete this Service Provider? This action cannot be undone.
                            </MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} style={{ color: 'gray' }}>Cancel</MDButton>
                        <MDButton type="submit" color="primary">Delete</MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

            <Dialog open={openDeactivateModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deactivateVendor}>
                    <DialogTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
                            </Grid>
                            <Grid item>
                                <MDTypography variant="h6" component="span">
                                    Confirm Deactivate Vendor
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent>
                        <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
                            <MDTypography variant="body1">
                                Are you sure you want to deactivate this Service Provider? This action cannot be undone.
                            </MDTypography>
                        </MDBox>

                        <MDBox mt={2}>
                            <MDTypography variant="body2">Please provide a reason for deactivation:</MDTypography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                margin="dense"
                                required
                                value={suspendReason}
                                onChange={(e) => setSuspendReason(e.target.value)}
                                placeholder="Enter your feedback here..."
                            />
                        </MDBox>

                        <MDBox mt={2}>
                            <MDTypography variant="body2">Suspend Until:</MDTypography>
                            <ReactDatePicker
                                selected={suspendUntil}
                                onChange={(date) => setSuspendUntil(date)}
                                minDate={new Date()}
                                dateFormat="yyyy-MM-dd"
                                customInput={<TextField fullWidth margin="dense" required />}
                            />
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} style={{ color: 'gray' }}>Cancel</MDButton>
                        <MDButton type="submit" color="primary">Deactivate</MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

            <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white !important' }}>
                    <MDTypography variant="h6" color="white">
                        Service Provider Details
                    </MDTypography>
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#fafafa', px: 3, m: 3 }}>
                    {selectedVendor && (
                        <div>
                            {[
                                {
                                    title: 'Basic Information',
                                    color: '#1976d2',
                                    bg: '#e3f2fd',
                                    fields: [
                                        { label: 'Service Type', value: selectedVendor.services_type },
                                        { label: 'Email', value: selectedVendor.email },
                                        { label: 'Owner Name', value: selectedVendor.owner_name },
                                        { label: 'Mobile Number', value: selectedVendor.mobile_number },
                                        { label: 'Keywords', value: selectedVendor.keywords },
                                    ],
                                },
                                {
                                    title: 'Experience & Certification',
                                    color: '#e65100',
                                    bg: '#fff3e0',
                                    fields: [
                                        { label: 'Experience (Years)', value: selectedVendor.experiance },
                                    ],
                                },
                                {
                                    title: 'Address & Availability',
                                    color: '#5e35b1',
                                    bg: '#ede7f6',
                                    fields: [
                                        { label: 'Home Address', value: selectedVendor.homeaddress },
                                        { label: 'Work Location', value: selectedVendor.worklocation },
                                        { label: 'Available Days', value: selectedVendor.availabiledays },
                                    ],
                                },
                                {
                                    title: 'Bank Details',
                                    color: '#8e24aa',
                                    bg: '#f3e5f5',
                                    fields: [
                                        { label: 'Account Number', value: selectedVendor.account_no },
                                        { label: 'IFSC Code', value: selectedVendor.ifsc_code },
                                        { label: 'Bank Name', value: selectedVendor.bank_name },
                                        { label: 'Branch Name', value: selectedVendor.branch_name },
                                        { label: 'Passbook Name', value: selectedVendor.passbook_name },
                                        { label: 'Pan Card Number', value: selectedVendor.pan_card_number },
                                    ],
                                },
                            ].map((section, idx) => (
                                <Card key={idx} sx={{ mb: 2, backgroundColor: section.bg }}>
                                    <CardContent sx={{ px: 3, py: 2 }}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            gutterBottom
                                            sx={{ color: section.color, fontSize: '1.1rem' }}
                                        >
                                            {section.title}
                                        </Typography>
                                        <Divider sx={{ mb: 2, backgroundColor: section.color }} />

                                        {section.fields.map((item, index) => (
                                            <Fragment key={index}>
                                                <Box mb={1}>
                                                    <Typography
                                                        variant="body3" color="textSecondary"
                                                    >
                                                        <strong>{item.label}:</strong>
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ ml: 1.5, fontSize: '0.95rem', color: '#333' }}
                                                    >
                                                        {item.value || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                {index !== section.fields.length - 1 && (
                                                    <Divider sx={{ mb: 1 }} />
                                                )}
                                            </Fragment>
                                        ))}

                                        {/* Specializations block */}
                                        {section.title === 'Basic Information' && (
                                            <Box mt={2}>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Specializations:
                                                </Typography>
                                                {(() => {
                                                    let parsedSpecializations = {};
                                                    try {
                                                        parsedSpecializations =
                                                            typeof selectedVendor.subservices === 'string'
                                                                ? JSON.parse(selectedVendor.subservices)
                                                                : selectedVendor.subservices;
                                                    } catch (e) {
                                                        console.error('Specializations parse error:', e);
                                                    }

                                                    return parsedSpecializations && typeof parsedSpecializations === 'object' ? (
                                                        Object.entries(parsedSpecializations).map(([category, specs], catIdx) => (
                                                            <Box key={catIdx} mt={1} ml={2}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ fontWeight: 'bold', color: '#512da8' }}
                                                                >
                                                                    {category}
                                                                </Typography>
                                                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                                    {String(specs[0])
                                                                        .split(',')
                                                                        .map((spec, i) => (
                                                                            <li key={i} style={{ fontSize: '0.9rem', color: '#424242' }}>
                                                                                {spec.trim()}
                                                                            </li>
                                                                        ))}
                                                                </ul>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ ml: 2, fontStyle: 'italic', color: '#999' }}
                                                        >
                                                            No specialization data
                                                        </Typography>
                                                    );
                                                })()}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: '#f5f5f5' }}>
                    <Button
                        onClick={handleCloseModal}
                        color="primary"
                        sx={{
                            fontWeight: 'bold',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isModalImageOpen} onClose={handleImageCloseModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2", textAlign: "center" }}>
                    Service Provider Images
                </DialogTitle>
                <DialogContent dividers>
                    <Tabs value={tabIndex} onChange={handleTabChange} centered>
                        <Tab label="Profile & Shop Photos" />
                        <Tab label="Experience Certificates" />
                        <Tab label="Gov ID Proof(Front & Back)" />
                    </Tabs>

                    {/* TAB 1: Single Image Edit */}
                    {tabIndex === 0 && (
                        <Grid container spacing={3} justifyContent="center" mt={1}>
                            {viewImages.profile_photo && (
                                <Grid item xs={12} sm={6}> {/* Changed to sm={6} for better spacing */}
                                    <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: "hidden" }}>
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={viewImages.profile_photo}
                                            alt="Profile"
                                            sx={{ transition: "0.3s", cursor: "pointer", "&:hover": { transform: "scale(1.05)" } }}
                                            onClick={() => setFullImage(viewImages.profile_photo)}
                                        />
                                        <CardContent sx={{ textAlign: "center" }}>
                                            <Typography variant="body1" fontWeight="bold" color="#1976d2" fontSize="16px">
                                                Profile Photo
                                            </Typography>
                                            <Box mt={1}>
                                                <MDButton color="warning" size="small" component="label" >
                                                    Edit
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => handleImageChange(e, "profile_photo", viewImages.mobile_number)}
                                                    />
                                                </MDButton>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {viewImages.work_place_gps && (
                                <Grid item xs={12} sm={6}> {/* Changed to sm={6} for better spacing */}
                                    <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: "hidden" }}>
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={viewImages.work_place_gps}
                                            alt="Workplace"
                                            sx={{ transition: "0.3s", cursor: "pointer", "&:hover": { transform: "scale(1.05)" } }}
                                            onClick={() => setFullImage(viewImages.work_place_gps)}
                                        />
                                        <CardContent sx={{ textAlign: "center" }}>
                                            <Typography variant="body1" fontWeight="bold" color="#1976d2" fontSize="16px">
                                                Work Place GPS Photo
                                            </Typography>
                                            <Box mt={1}>
                                                <MDButton color="warning" size="small" component="label">
                                                    Edit
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => handleImageChange(e, "work_place_gps", viewImages.mobile_number)}
                                                    />
                                                </MDButton>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* TAB 2: Experience Certificates */}
                    {tabIndex === 1 && (
                        <Grid container spacing={3} justifyContent="center" mt={1}>
                            {viewImages.experience_certificates &&
                                viewImages.experience_certificates.replace(/\\/g, "/").split(",").map((image, index) => (
                                    <Grid item xs={12} sm={4} key={index}>
                                        <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: "hidden" }}>
                                            <CardMedia
                                                component="img"
                                                height="180"
                                                image={image.trim()}
                                                alt={`Experience Certificate ${index + 1}`}
                                                sx={{ transition: "0.3s", cursor: "pointer", "&:hover": { transform: "scale(1.05)" } }}
                                                onClick={() => setFullImage(image.trim())}
                                            />
                                            <CardContent sx={{ textAlign: "center" }}>
                                                <Typography variant="body1" fontWeight="bold" color="#1976d2" fontSize="16px">
                                                    Experience Certificate {index + 1}
                                                </Typography>
                                                <Box mt={1} display="flex" justifyContent="center">
                                                    <MDButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleImageRemove("experience_certificates", index)}
                                                    >
                                                        Remove
                                                    </MDButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}

                            <Grid item xs={12} sm={4}>
                                <MDButton color="success" size="small" component="label">
                                    Add New Certificate
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => handleImageAdd(e, "experience_certificates")}
                                    />
                                </MDButton>
                            </Grid>
                        </Grid>
                    )}

                    {/* TAB 3: ID Proof */}
                    {tabIndex === 2 && (
                        <Grid container spacing={3} justifyContent="center" mt={1}>
                            {viewImages.idproof_photo &&
                                viewImages.idproof_photo.replace(/\\/g, "/").split(",").map((image, index) => (
                                    <Grid item xs={12} sm={4} key={index}>
                                        <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: "hidden" }}>
                                            <CardMedia
                                                component="img"
                                                height="180"
                                                image={image.trim()}
                                                alt={`ID Proof ${index + 1}`}
                                                sx={{ transition: "0.3s", cursor: "pointer", "&:hover": { transform: "scale(1.05)" } }}
                                                onClick={() => setFullImage(image.trim())}
                                            />
                                            <CardContent sx={{ textAlign: "center" }}>
                                                <Typography variant="body1" fontWeight="bold" color="#1976d2" fontSize="16px">
                                                    ID Proof {index + 1}
                                                </Typography>
                                                <Box mt={1} display="flex" justifyContent="center">
                                                    <MDButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleImageRemove("idproof_photo", index)}
                                                    >
                                                        Remove
                                                    </MDButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}

                            <Grid item xs={12} sm={4}>
                                <MDButton color="success" size="small" component="label">
                                    Add New ID Proof
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) => handleImageAdd(e, "idproof_photo")}
                                    />
                                </MDButton>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", paddingBottom: 2 }}>
                    <MDButton onClick={handleImageCloseModal} color="primary">
                        Close
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Full Image Modal */}
            <Dialog open={Boolean(fullImage)} onClose={() => setFullImage(null)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>Full Image</DialogTitle>
                <DialogContent sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <img src={fullImage} alt="Full" style={{ maxWidth: "100%", maxHeight: "90vh" }} />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <MDButton onClick={() => setFullImage(null)} color="secondary">
                        Close
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert elevation={6} variant="filled" severity={snackbar.severity}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </DashboardLayout>
    );
}

export default ServiceRegistration;